import React from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { useApiSqlContext } from 'src/modules/contexts/ApiSqlContext';

// Phosphor icons
import {
    MicrosoftExcelLogo,
    FilePdf,
    Image,
    File,
} from '@phosphor-icons/react';

interface AttachmentViewerProps {
    attachments: string[]; // Array of file URLs
    basePath: string; // Path to split the file URL (e.g., "/alerts_messages_uploads/")
}

export const processAttachment = (
    fileNameOrUrl: string,
    basePath: string = '',
    displayLength: number
) => {
    // Extract filename from URL if a basePath is provided
    const filename = basePath
        ? fileNameOrUrl.split(basePath)[1]?.split('/')?.pop()
        : fileNameOrUrl;

    // Determine file extension
    const fileExtension = filename?.split('.').pop()?.toLowerCase() || '';

    // Choose the correct icon and color
    let fileIcon;
    let iconColor;

    if (['pdf'].includes(fileExtension)) {
        fileIcon = FilePdf;
        iconColor = '#C8102F'; // Red for PDFs
    } else if (['xls', 'xlsx', 'csv'].includes(fileExtension)) {
        fileIcon = MicrosoftExcelLogo;
        iconColor = '#27a035'; // Green for Excel and CSV files
    } else if (['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension)) {
        fileIcon = Image;
        iconColor = '#1D4A74'; // Blue for images
    } else {
        fileIcon = File; // Default icon
        iconColor = '#6C757D'; // Gray for unknown file types
    }

    // Shorten the filename for display
    const maxDisplayLength = displayLength;
    const shortFilename =
        filename && filename.length > maxDisplayLength
            ? `...${filename.slice(-maxDisplayLength)}`
            : filename;

    return { filename, fileIcon, iconColor, shortFilename };
};

const AttachmentViewer: React.FC<AttachmentViewerProps> = ({
    attachments,
    basePath,
}) => {
    const { user } = useAuthenticator(context => [context.user]);
    const { client } = useApiSqlContext();

    const handleDownload = async (fileUrl: string) => {
        try {
            if (!client) {
                throw new Error('API client is not initialized');
            }

            const token = user
                .getSignInUserSession()
                ?.getIdToken()
                .getJwtToken();
            if (!token) {
                throw new Error('AWS Amplify user token cannot be undefined');
            }

            // Fetch the file data from the backend
            const downloadResponse =
                await client.default.getDownloadS3BucketFile(token, fileUrl);
            const fileContent: string =
                downloadResponse.body.file_bytes_base_64;

            // Decode the Base64 string to a byte array
            const byteCharacters = atob(fileContent);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);

            // Create a Blob from the byte array
            const blob = new Blob([byteArray], {
                type: 'application/octet-stream',
            });

            // Create a URL from the Blob and download the file
            const fileBlobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = fileBlobUrl;
            link.download = fileUrl.split('/').pop() || 'file';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Revoke the URL after download to free up memory
            URL.revokeObjectURL(fileBlobUrl);
        } catch (error) {
            console.error('Error downloading the file:', error);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {attachments.map((fileUrl, index) => {
                const { shortFilename, fileIcon, iconColor } =
                    processAttachment(fileUrl, basePath, 8);
                // Assign the icon component from Phosphor to a variable
                const IconComponent = fileIcon;

                return (
                    <Box
                        key={index}
                        sx={{
                            borderRadius: '12px',
                            border: '2px solid #4D4D4D',
                            p: 1,
                            cursor: 'pointer',
                            bgcolor: '#F2F2F2',
                            width: '100px',
                            textAlign: 'center',
                        }}
                        onClick={() => handleDownload(fileUrl)}
                    >
                        <Stack spacing={1} alignItems="center">
                            <IconComponent
                                size={32}
                                color={iconColor}
                                weight="fill"
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '100%',
                                }}
                                title={shortFilename} // Show full name on hover
                            >
                                {shortFilename || 'Download File'}
                            </Typography>
                        </Stack>
                    </Box>
                );
            })}
        </Box>
    );
};

export default AttachmentViewer;
